import React, { useState, useEffect, useMemo, useCallback } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { Link, useLocation } from "react-router-dom";
import { FaHome, FaTimes } from "react-icons/fa";
import { useAuth } from "../context/auth/AuthContext";
import { canAccess, canAccessReport } from "../helpers/permissionHelper";

const Sidebar = ({ sidebarOpen, onCloseSidebar = () => {} }) => {
    const [openMenus, setOpenMenus] = useState({});
    const [openSubMenus, setOpenSubMenus] = useState({});

    const location = useLocation();

    const pathname = location.pathname;

    const { permissions } = useAuth();

    const allMenus = useMemo(() => [
        {
            name: "Properti",
            path: "/properti",
            icon: "🏠",
            key: "properti",
        },
        {
            name: "Kamar",
            path: "/kamar",
            icon: "🛏️",
            key: "kamar",
        },
        {
            name: "Penyewa",
            path: "/penyewa",
            icon: "🧑‍💼",
            key: "penyewa",
        },
        {
            name: "Pengeluaran",
            path: "/pengeluaran",
            icon: "🧾",
            key: "pengeluaran",
        },
        {
            name: "Kas Operasional",
            path: "/kas-operasional",
            icon: "💵",
            key: "kas_operasional",
        },
        {
            name: "Laporan Keuangan",
            icon: "📊",
            key: "laporan",
            subMenus: [
                {
                    name: "Arus Kas",
                    path: "/laporan-arus-kas",
                    key: "arus_kas",
                },
                {
                    name: "Laba Rugi",
                    path: "/laporan-laba-rugi",
                    key: "laba_rugi",
                },
                {
                    name: "Buku Besar",
                    path: "/laporan-buku-besar",
                    key: "buku_besar",
                },
                {
                    name: "Piutang",
                    path: "/laporan-piutang",
                    key: "piutang",
                },
                {
                    name: "Mutasi Kas Operasional",
                    path: "/laporan-mutasi-kas-operasional",
                    key: "mutasi_kas_operasional",
                },
            ],
        },
    ], []);

    // =========================
    // FILTER MENU BERDASARKAN PERMISSION
    // =========================
    const menus = useMemo(() => {
        if (!permissions) return [];

        return allMenus
            .map((menu) => {

                // Menu dengan submenu
                if (menu.subMenus) {

                    const filteredSubMenus = menu.subMenus.filter((sub) =>
                        canAccessReport(sub.key, permissions)
                    );

                    return filteredSubMenus.length > 0
                        ? { ...menu, subMenus: filteredSubMenus }
                        : null;
                }

                // Menu biasa
                return canAccess(menu.key, permissions)
                    ? menu
                    : null;
            })
            .filter(Boolean);

    }, [permissions, allMenus]);

    // =========================
    // TOGGLE MENU
    // =========================
    const toggleMenu = (i) => {
        setOpenMenus((prev) => ({
            ...prev,
            [i]: !prev[i],
        }));
    };

    const toggleNested = (pi, si) => {
        setOpenSubMenus((prev) => ({
            ...prev,
            [pi]: {
                ...(prev[pi] || {}),
                [si]: !prev[pi]?.[si],
            },
        }));
    };

    // =========================
    // ACTIVE PATH
    // =========================
    const isPathActive = useCallback(
        (path) => pathname === path || pathname.startsWith(path),
        [pathname]
    );

    const isMenuActive = useCallback((menu) => {

        if (menu.path && isPathActive(menu.path)) {
            return true;
        }

        if (menu.subMenus) {
            return menu.subMenus.some((sub) => {

                if (sub.path && isPathActive(sub.path)) {
                    return true;
                }

                if (sub.subMenus) {
                    return sub.subMenus.some((ss) =>
                        isPathActive(ss.path)
                    );
                }

                return false;
            });
        }

        return false;

    }, [isPathActive]);

    // =========================
    // AUTO OPEN ACTIVE MENU
    // =========================
    useEffect(() => {

        const open = {};

        const subOpen = {};

        menus.forEach((menu, i) => {

            if (isMenuActive(menu)) {
                open[i] = true;
            }

            menu.subMenus?.forEach((sub, si) => {

                if (sub.subMenus) {

                    sub.subMenus.forEach((ss) => {

                        if (isPathActive(ss.path)) {

                            open[i] = true;

                            subOpen[i] = {
                                ...(subOpen[i] || {}),
                                [si]: true,
                            };
                        }
                    });
                }
            });
        });

        setOpenMenus(open);

        setOpenSubMenus(subOpen);

    }, [pathname, menus, isMenuActive, isPathActive]);

    const closeOnMobile = () => {
        if (window.innerWidth <= 991) {
            onCloseSidebar();
        }
    };

    return (
        <div className={`sidebar-shell ${sidebarOpen ? "sidebar-open" : ""}`}>
            <div
                className={`sidebar-overlay ${sidebarOpen ? "show" : ""}`}
                onClick={onCloseSidebar}
                role="button"
                aria-label="Tutup menu"
            />

            <div
                className="sidebar-panel d-flex flex-column p-3 bg-light border-end"
                style={{
                    width: "260px",
                    minWidth: "260px",
                    flexShrink: 0,
                    height: "100vh",
                    overflowY: "auto",
                }}
            >

            {/* ========================= */}
            {/* CUSTOM STYLE */}
            {/* ========================= */}
            <style>{`
                .sidebar-shell{
                    position: relative;
                    z-index: 1000;
                }


                .sidebar-overlay{
                    display: none;
                    position: fixed;
                    inset: 0;
                    z-index: 1050;
                    background: rgba(0, 0, 0, 0.45);
                    opacity: 0;
                    transition: opacity .2s ease;
                    pointer-events: none;
                }

                .sidebar-overlay.show{
                    display: block;
                    opacity: 1;
                    pointer-events: auto;
                }

                .sidebar-panel{
                    position: relative;
                    transition: transform .2s ease, visibility .2s ease;
                    background: #fff;
                    z-index: 1100;
                }

                .sidebar-close-button{
                    position: absolute;
                    top: 1rem;
                    right: 1rem;
                    border-radius: 12px;
                    width: 38px;
                    height: 38px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .sidebar-menu{
                    transition: transform .15s ease, box-shadow .15s ease, filter .15s ease;
                }

                .sidebar-menu:hover{
                    filter: brightness(1.08);
                    transform: translateX(6px);
                    box-shadow: 0 6px 16px rgba(124, 58, 237, 0.3);
                }

                .sidebar-submenu{
                    transition: transform .15s ease, background-color .15s ease, box-shadow .15s ease;
                    font-size: 0.84rem;
                    color: #334155;
                }

                .sidebar-submenu:hover{
                    background: linear-gradient(135deg, rgba(124,58,237,0.08), rgba(167,139,250,0.08));
                    transform: translateX(6px);
                    border-left: 3px solid #7c3aed;
                    padding-left: 7px;
                }

                .submenu{
                    display: none;
                }

                .submenu.show{
                    display: block;
                }

                @media (max-width: 991px) {
                    .sidebar-panel{
                        position: fixed !important;
                        top: 0;
                        left: 0;
                        bottom: 0;
                        width: 80%;
                        max-width: 320px;
                        min-width: auto;
                        transform: translateX(-100%);
                        box-shadow: 8px 0 30px rgba(15,23,42,0.18);
                        overflow-y: auto;
                    }

                    .sidebar-shell.sidebar-open .sidebar-panel{
                        transform: translateX(0);
                    }

                    /* Sidebar is controlled by Navbar toggler; no hamburger here */

                    .sidebar-close-button{
                        display: flex;
                    }
                }

                @media (min-width: 992px) {
                    .sidebar-hamburger{
                        display: none !important;
                    }

                    .sidebar-overlay{
                        display: none !important;
                    }

                    .sidebar-panel{
                        position: sticky;
                        top: 0;
                        transform: none !important;
                    }

                    .sidebar-close-button{
                        display: none !important;
                    }
                }

            `}</style>

            <button
                type="button"
                className="btn btn-light sidebar-close-button"
                onClick={onCloseSidebar}
                aria-label="Tutup menu"
            >
                <FaTimes />
            </button>

            {/* ========================= */}
            {/* LOGO */}
            {/* ========================= */}
            <div className="mb-3 px-2">
                <h4
                    style={{
                        fontWeight: "800",
                        letterSpacing: "1px",
                        margin: 0,
                        color: "#000",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                    }}
                >
                    <FaHome style={{ fontSize: "1.2rem" }} />
                    KOS
                </h4>
            </div>

            {/* ========================= */}
            {/* MENU */}
            {/* ========================= */}
            <ul className="nav flex-column">

                {menus.map((menu, i) => (

                    <li key={i} className="nav-item mb-1">

                        {menu.subMenus ? (
                            <>
                                {/* ========================= */}
                                {/* MAIN MENU */}
                                {/* ========================= */}
                                <button
                                    className="btn w-100 text-start sidebar-menu"
                                    onClick={() => toggleMenu(i)}
                                    style={{
                                        background: "linear-gradient(135deg, #7c3aed 0%, #a78bfa 50%, #c4b5fd 100%)",
                                        color: "#fff",
                                        borderRadius: "8px",
                                        fontWeight: 600,
                                        border: "none",
                                        boxShadow: "0 4px 12px rgba(124, 58, 237, 0.2)",
                                    }}
                                >
                                    {menu.icon} {menu.name}
                                </button>

                                {/* ========================= */}
                                {/* SUB MENU */}
                                {/* ========================= */}
                                <div className={`submenu ${openMenus[i] ? "show" : ""}`}>

                                    <ul
                                        className="nav flex-column ms-3 mt-2"
                                        style={{
                                            borderLeft: "2px solid #7c3aed",
                                            background: "linear-gradient(90deg, transparent, rgba(124,58,237,0.02))",
                                            paddingLeft: "10px",
                                        }}
                                    >

                                        {menu.subMenus.map((sub, si) => (

                                            <li key={si}>

                                                {sub.subMenus ? (
                                                    <>
                                                        <button
                                                            className="btn w-100 text-start sidebar-submenu"
                                                            onClick={() => toggleNested(i, si)}
                                                        >
                                                            {sub.icon} {sub.name}
                                                        </button>

                                                        <div className={`submenu ${openSubMenus[i]?.[si] ? "show" : ""}`}>

                                                            <ul
                                                                className="nav flex-column ms-3"
                                                                style={{
                                                                    borderLeft: "2px solid #a78bfa",
                                                                    background: "linear-gradient(90deg, transparent, rgba(167,139,250,0.02))",
                                                                }}
                                                            >

                                                                {sub.subMenus.map((ss, ssi) => (

                                                                    <li key={ssi}>

                                                                        <Link
                                                                            to={ss.path}
                                                                            className="nav-link sidebar-submenu"
                                                                            onClick={closeOnMobile}
                                                                            style={{
                                                                                ...(isPathActive(ss.path) && {
                                                                                    background: "linear-gradient(135deg, rgba(124,58,237,0.15), rgba(167,139,250,0.1))",
                                                                                    fontWeight: "bold",
                                                                                    borderLeft: "3px solid #7c3aed",
                                                                                    paddingLeft: "7px",
                                                                                }),
                                                                            }}
                                                                        >
                                                                            {ss.icon} {ss.name}
                                                                        </Link>
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        </div>
                                                    </>
                                                ) : (
                                                    <Link
                                                        to={sub.path}
                                                        className="nav-link sidebar-submenu"
                                                        onClick={closeOnMobile}
                                                        style={{
                                                            ...(isPathActive(sub.path) && {
                                                                background: "linear-gradient(135deg, rgba(124,58,237,0.15), rgba(167,139,250,0.1))",
                                                                fontWeight: "bold",
                                                                borderLeft: "3px solid #7c3aed",
                                                                paddingLeft: "7px",
                                                            }),
                                                        }}
                                                    >
                                                        {sub.icon} {sub.name}
                                                    </Link>
                                                )}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </>
                        ) : (
                            <Link
                                to={menu.path}
                                className="nav-link sidebar-menu"
                                onClick={closeOnMobile}
                                style={{
                                    background: "linear-gradient(135deg, #7c3aed 0%, #a78bfa 50%, #c4b5fd 100%)",
                                    color: "#fff",
                                    borderRadius: "8px",
                                    fontWeight: 600,
                                    border: "none",
                                    boxShadow: "0 4px 12px rgba(124, 58, 237, 0.2)",
                                    textDecoration: "none",
                                }}
                            >
                                {menu.icon} {menu.name}
                            </Link>
                        )}
                    </li>
                ))}
            </ul>
        </div>
    </div>
    );
};

export default Sidebar;